import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Mail, Eye, Send, Loader2, X } from "lucide-react";

export default function NewsletterAdmin() {
  const { user } = useAuth();
  const [testEmail, setTestEmail] = useState(user?.email || "");
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const sendTest = async () => {
    if (!testEmail) return;
    setSending(true);
    try {
      const { data } = await api.post("/newsletter/send-test", { email: testEmail });
      toast.success(`Test sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed. (Resend test mode only sends to your Resend signup email until you verify a domain.)");
    } finally {
      setSending(false);
    }
  };

  const previewIssue = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.get("/newsletter/preview");
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="brutal-border bg-[#0a0a0a] p-6" data-testid="newsletter-admin">
      <div className="flex items-center gap-3 mb-4">
        <Mail className="w-5 h-5 text-[#FF3333]" />
        <h2 className="font-heading uppercase text-xl">Weekly Newsletter</h2>
      </div>
      <p className="font-sub text-white/60 text-sm leading-relaxed">
        Every Monday at 09:00 UTC, FORGE-AI auto-generates a fresh issue (training, health, anime, drops) and sends it to all subscribers. Preview or send yourself a test issue right now.
      </p>

      <div className="mt-5 flex flex-wrap gap-2 items-stretch">
        <input
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          placeholder="your@email.com"
          data-testid="newsletter-test-email"
          className="flex-1 min-w-[200px] bg-transparent border border-white/15 focus:border-[#FF3333] outline-none px-3 py-2 font-sub text-sm transition-colors"
        />
        <button
          onClick={sendTest}
          disabled={sending}
          data-testid="newsletter-send-test"
          className="btn-forged-red text-xs px-4 disabled:opacity-50"
        >
          {sending ? <><Loader2 className="w-3 h-3 animate-spin" /> Sending</> : <><Send className="w-3 h-3" /> Send Test</>}
        </button>
        <button
          onClick={previewIssue}
          disabled={previewing}
          data-testid="newsletter-preview"
          className="btn-forged-blue text-xs px-4 disabled:opacity-50"
        >
          {previewing ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating</> : <><Eye className="w-3 h-3" /> Preview In-App</>}
        </button>
      </div>

      <div className="font-mono text-[9px] tracking-widest text-white/30 mt-3">
        TEST MODE: Resend's default sender only delivers to your verified Resend email. Verify forgedathletics.fit on Resend to send to anyone.
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" data-testid="newsletter-preview-modal">
          <div className="bg-[#0a0a0a] brutal-border max-w-3xl w-full max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setPreview(null)}
              data-testid="newsletter-preview-close"
              className="sticky top-2 right-2 ml-auto flex w-8 h-8 items-center justify-center bg-black/80 border border-white/20 text-white hover:text-[#FF3333] z-10"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="p-4 border-b border-white/10 font-mono text-xs text-white/50">
              SUBJECT: <span className="text-white">{preview.issue?.subject}</span>
            </div>
            <iframe
              title="Newsletter Preview"
              srcDoc={preview.html}
              className="w-full"
              style={{ height: "70vh", border: 0, background: "#050505" }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
